/*
 * ---------------------------------------------------------------------------
 * zap-seal-a11y.js
 * Zenyth accessibility repair shim for the Seal Subscriptions app widget.
 *
 * WHY THIS FILE EXISTS
 * The 2026-07-28 full-site audit of shop.zenythgroup.com recorded 30 defects
 * owned by the third-party Seal Subscriptions app (see
 * clients/shop_zenythgroup/audits/_MASTER-REPORT.md, root causes 3, 8 and 9,
 * plus the live pass). The app markup is vendor-generated and injected at
 * runtime, so it cannot be fixed in Liquid. Zenyth does not control the
 * vendor's release cycle, so this shim repairs the rendered DOM from outside.
 *
 * WHAT IT REPAIRS (and the WCAG criterion each one answers)
 *   R1  4.1.2 Name, Role, Value (Serious, 11 pages)
 *       Seal renders  <div role="radio" tabindex="0"> <input type="radio"> </div>
 *       A div with role="radio" that CONTAINS a native radio is a nested
 *       interactive control: assistive technology sees two radios for one
 *       choice, and the focus order stops on the fake one first. The repair
 *       strips the fake ARIA layer (role, tabindex, aria-checked) from the
 *       wrapper and lets the native <input type="radio"> be the control, which
 *       is already correctly grouped by its shared name attribute. The
 *       enclosing role="radiogroup" is demoted to role="group" for the same
 *       reason: a radiogroup with no ARIA radio children is a broken
 *       relationship, while group is accurate and keeps the label association.
 *
 *   R2  4.1.2 Name, Role, Value (Moderate, 7 pages)
 *       An unresolved Seal template token leaks into the visible label and
 *       therefore into the accessible name, e.g.
 *       "{## A11YLABS !##} Yearly Subscription". The repair removes the
 *       {## ... !##} token from the label text.
 *       NOTE: the REAL fix is in the Seal app admin, where the plan label
 *       carries the literal token. This is a safety net, not a substitute. It
 *       is deliberately conservative: it only ever deletes a token that matches
 *       the exact {## ... !##} shape, never other text.
 *
 *   R3  2.4.3 Focus Order (Minor, 11 pages)
 *       Seal detail / more-info <span> elements carry tabindex="0" with no role
 *       and no accessible name, producing empty focus stops on the way to the
 *       real controls. The repair removes tabindex from any Seal span that has
 *       no role, no href and no accessible name. A span that DOES expose a
 *       role or a name is left alone, because that one is a real control.
 *
 *   The fourth Seal defect (1.4.10 Reflow, the option table overflowing at
 *   320px) is a styling problem and lives in assets/zap-seal-a11y.css.
 *
 * DESIGN RULES THIS FILE FOLLOWS (per Alan, 2026-07-30)
 *   - Respect the widget's load timing. Seal injects late and re-renders on
 *     variant change, so the shim waits for the widget instead of assuming it,
 *     and re-runs when the widget mutates.
 *   - Debounce. Seal can emit dozens of mutations in one render; the repair
 *     runs at most once per DEBOUNCE_MS.
 *   - Disconnect the MutationObserver. It never observes forever: it stops
 *     after STABLE_PASSES consecutive passes that changed nothing, or at
 *     MAX_WATCH_MS, whichever comes first. It re-arms only on a real signal
 *     (variant change or a Seal re-render event).
 *   - Additive and idempotent. It only removes bogus attributes and a literal
 *     token. It never unwraps, moves, reorders or deletes a node, never
 *     rewrites Seal's own behaviour, and marks what it has touched so a second
 *     pass is a no-op.
 *   - Never throws. Every pass is wrapped, because a shim that breaks the page
 *     is worse than the defect it fixes.
 *
 * SCOPE / LIMITS (be honest about these)
 *   - This does NOT make Seal conformant. It repairs what is repairable from
 *     outside. The roving-radio keyboard model, arrow-key selection and the
 *     announced text still need a manual screen-reader and keyboard pass.
 *   - If Seal ships its own fix, re-test and delete the corresponding repair
 *     here rather than leaving two layers fighting.
 *
 * Zenyth changelog reference: shop_zenythgroup CHANGELOG 2026-07-30, audit
 * fixes E1, E2, E3. Owner: Zenyth engineering. Remove only after re-audit.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* Tunables. Kept at the top so timing can be adjusted without reading the logic. */
  var DEBOUNCE_MS = 150; // collapse a burst of Seal mutations into one repair pass
  var MAX_WATCH_MS = 20000; // hard ceiling on how long we observe after a trigger
  var STABLE_PASSES = 4; // consecutive no-change passes before we disconnect
  var BOOT_POLL_MS = 400; // how often we look for the widget before it exists
  var BOOT_TIMEOUT_MS = 15000; // give up looking for a widget that is not installed
  var FLAG = 'data-znt-seal-a11y'; // idempotence marker
  var TOKEN_RE = /\{##[\s\S]*?!##\}/g; // the exact Seal template-token shape, nothing else
  var DEBUG = /[?&]znt-debug=seal/.test(window.location.search);

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[zap-seal-a11y]');
    // eslint-disable-next-line no-console
    console.log.apply(console, args);
  }

  /* Seal's markup is not namespaced consistently, so we look for the classes the
     audit actually observed in the rendered DOM, plus the generic sls- prefix. */
  function sealRoots() {
    return document.querySelectorAll(
      '.sls-option-container, .sls-widget, [class^="sls-"], [class*=" sls-"]'
    );
  }

  function widgetPresent() {
    return sealRoots().length > 0;
  }

  function accessibleNameish(el) {
    // Cheap, deliberately conservative: enough to tell "empty focus stop" from
    // "real control". Not a full accname implementation.
    return (
      (el.getAttribute('aria-label') || '').trim() ||
      (el.getAttribute('aria-labelledby') || '').trim() ||
      (el.getAttribute('title') || '').trim() ||
      (el.textContent || '').trim()
    );
  }

  /* R1: strip the fake radio layer that wraps a native radio. */
  function repairNestedRadios(root, stats) {
    var wrappers = root.querySelectorAll('[role="radio"]');
    for (var i = 0; i < wrappers.length; i++) {
      var w = wrappers[i];
      if (w.getAttribute(FLAG) === 'radio') continue;
      // Only touch a wrapper that actually contains a native radio. A standalone
      // role="radio" with no native input is a legitimate ARIA widget: leave it.
      if (!w.querySelector('input[type="radio"]')) continue;
      w.removeAttribute('role');
      w.removeAttribute('aria-checked');
      if (w.hasAttribute('tabindex')) w.removeAttribute('tabindex');
      w.setAttribute(FLAG, 'radio');
      stats.radios++;
    }

    // A radiogroup whose ARIA radio children we just removed is a broken
    // relationship. role="group" keeps the grouping and its label, honestly.
    var groups = root.querySelectorAll('[role="radiogroup"]');
    for (var g = 0; g < groups.length; g++) {
      var grp = groups[g];
      if (grp.getAttribute(FLAG) === 'group') continue;
      if (grp.querySelector('[role="radio"]')) continue; // still a real radiogroup
      if (!grp.querySelector('input[type="radio"]')) continue;
      grp.setAttribute('role', 'group');
      grp.setAttribute(FLAG, 'group');
      stats.groups++;
    }
  }

  /* R2: remove the unresolved {## ... !##} template token from labels. */
  function repairLeakedTokens(root, stats) {
    // Walk text nodes so we never touch element structure or Seal's listeners.
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return node.nodeValue && node.nodeValue.indexOf('{##') !== -1
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });
    var node;
    var hits = [];
    while ((node = walker.nextNode())) hits.push(node);
    for (var i = 0; i < hits.length; i++) {
      var t = hits[i];
      var cleaned = t.nodeValue.replace(TOKEN_RE, '').replace(/\s{2,}/g, ' ').trim();
      if (cleaned === t.nodeValue) continue;
      t.nodeValue = cleaned;
      stats.tokens++;
    }

    // The token can also sit inside an author-supplied aria-label.
    var labelled = root.querySelectorAll('[aria-label*="{##"]');
    for (var j = 0; j < labelled.length; j++) {
      var el = labelled[j];
      var v = el.getAttribute('aria-label');
      var c = v.replace(TOKEN_RE, '').replace(/\s{2,}/g, ' ').trim();
      if (c === v) continue;
      if (c) el.setAttribute('aria-label', c);
      else el.removeAttribute('aria-label');
      stats.tokens++;
    }
  }

  /* R3: drop tabindex from Seal spans that are not controls. */
  function repairEmptyFocusStops(root, stats) {
    var stops = root.querySelectorAll('span[tabindex="0"]');
    for (var i = 0; i < stops.length; i++) {
      var s = stops[i];
      if (s.getAttribute(FLAG) === 'tabindex') continue;
      if (s.hasAttribute('role')) continue; // declares itself a control, leave it
      if (s.hasAttribute('href')) continue;
      if (accessibleNameish(s)) continue; // has a name, so it is reachable for a reason
      s.removeAttribute('tabindex');
      s.setAttribute(FLAG, 'tabindex');
      stats.stops++;
    }
  }

  function runPass() {
    var stats = { radios: 0, groups: 0, tokens: 0, stops: 0 };
    var roots = sealRoots();
    for (var i = 0; i < roots.length; i++) {
      try {
        repairNestedRadios(roots[i], stats);
        repairLeakedTokens(roots[i], stats);
        repairEmptyFocusStops(roots[i], stats);
      } catch (err) {
        // One bad node must never stop the rest, and must never break the page.
        log('pass error, continuing', err);
      }
    }
    var changed = stats.radios + stats.groups + stats.tokens + stats.stops;
    if (changed) log('repaired', stats);
    return changed;
  }

  /* ---- observation lifecycle: debounce, then disconnect ---- */
  var observer = null;
  var debounceTimer = null;
  var watchdogTimer = null;
  var stableCount = 0;

  function stopWatching(reason) {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (watchdogTimer) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
    log('observer disconnected:', reason);
  }

  function scheduledPass() {
    debounceTimer = null;
    var changed = runPass();
    if (changed) {
      stableCount = 0;
      return;
    }
    stableCount++;
    if (stableCount >= STABLE_PASSES) stopWatching('stable');
  }

  function onMutations() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(scheduledPass, DEBOUNCE_MS);
  }

  function startWatching(reason) {
    runPass(); // repair whatever is already on the page before observing
    if (observer) {
      // Already watching: just extend the window and reset stability.
      stableCount = 0;
      log('watch extended:', reason);
      return;
    }
    stableCount = 0;
    observer = new MutationObserver(onMutations);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'tabindex', 'aria-checked', 'aria-label', 'class']
    });
    watchdogTimer = setTimeout(function () {
      stopWatching('max watch window reached');
    }, MAX_WATCH_MS);
    log('watching:', reason);
  }

  /* ---- boot: wait for the widget instead of assuming it ---- */
  function boot() {
    var waited = 0;
    if (widgetPresent()) {
      startWatching('widget present at boot');
    } else {
      var poll = setInterval(function () {
        waited += BOOT_POLL_MS;
        if (widgetPresent()) {
          clearInterval(poll);
          startWatching('widget appeared after ' + waited + 'ms');
        } else if (waited >= BOOT_TIMEOUT_MS) {
          clearInterval(poll);
          log('no Seal widget on this page, standing down');
        }
      }, BOOT_POLL_MS);
    }

    /* Re-arm on the signals that make Seal re-render. Each is a real event, so
       we are not polling forever. */
    document.addEventListener('variant:update', function () {
      startWatching('variant:update');
    });
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('variant-picker, [class^="sls-"], [class*=" sls-"]')) {
        startWatching('selection changed');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
